import { useCallback, useEffect, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { type EmbeddedWebSdk, Fourthline } from '#providers/components/fourthline';
import type { Locale } from '#providers/utils/locale';
import { useReadinessSpan } from '@repo/shared-kit/hooks/use-readiness-span';
import { CFT } from '@repo/runtime-kit/sentry/sentry-tags';
import { useSentry } from '@repo/runtime-kit/sentry/context';
import { useConfig } from '@repo/shared-kit/config/config-provider';
import { Button } from '@repo/design-system-kit/components/button/button';
import { KycProcessStatus, KycProvider, kycQueries } from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context/kyc-context';
import { useKycCompletion } from '#hooks/use-kyc-completion';
import { useProviderDetails } from '#hooks/use-provider-details';
import { useKycTracking } from '#tracking/use-kyc-tracking';
import { KycError } from '#error/error';
import { FourthlineLoading } from '#loading/fourthline-loading';
import { isKycExpired } from '#utils/is-kyc-expired';
import styles from './fourthline-provider.module.css';

export function FourthlineProvider() {
  const { locale } = useIntl();
  const sentry = useSentry();
  const { environment } = useConfig();
  const canSkipKyc = !environment.isProduction;

  const { legalCountry, trackingPrefix, onBack, onComplete, onError, isRegisterFlow } =
    useKycContext();
  const events = useKycTracking();
  const market = isRegisterFlow ? legalCountry : undefined;
  const {
    data: kycInfo,
    refetch: refetchAcceptance,
    error: kycError,
    isError: isKycError,
  } = useSuspenseQuery(kycQueries.item({ market }));

  const {
    data: providerDetails,
    refetch,
    error: providerDetailsError,
    isLoading,
  } = useProviderDetails({
    provider: KycProvider.fourthline,
    identityVerificationId: kycInfo.activeProcess?.identityVerificationId,
    refetchAcceptance,
  });

  useEffect(() => {
    if (providerDetailsError) {
      onError?.(providerDetailsError);
    }
  }, [providerDetailsError, onError]);

  const token = providerDetails?.token ?? '';
  const redirectUrl = providerDetails?.redirectUrl ?? '';
  const processStatus = kycInfo.activeProcess?.processStatus;

  useReadinessSpan('kyc.fourthline', {
    isLoading,
    isError: !token || !redirectUrl || isKycError,
    error: (kycError ?? !token) ? new Error('Missing Fourthline credentials') : undefined,
  });

  const markKycCompletionFired = useKycCompletion(processStatus, () =>
    onComplete(KycProvider.fourthline)
  );

  const handleOnboardingStarted = useCallback(() => {
    events.fourthlineVerificationScreenLoaded();
  }, [events]);

  const handleOnboardingCompleted = useCallback(() => {
    markKycCompletionFired();

    onComplete(KycProvider.fourthline);
  }, [onComplete, markKycCompletionFired]);

  const handleOnboardingCompletedError = useCallback(() => {
    sentry.captureMessage('Fourthline onboarding completed with error', {
      level: 'error',
      tags: { component: 'FourthlineProvider', provider: KycProvider.fourthline },
      cft: CFT.IDENTITY_VERIFICATION,
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus,
        hasProviderDetails: Boolean(providerDetails),
      },
    });
  }, [sentry, kycInfo, processStatus, providerDetails]);

  const handleOnboardingRestartRequest = useCallback(
    async (webSdkElement: { setToken: (token: string) => void }) => {
      events.fourthlineRegenerateLinkClicked();

      const { data } = await refetch();
      if (!data) return;

      if ('providerToken' in data) {
        webSdkElement.setToken(data.providerToken);
        return;
      }

      const contextToken = data.activeProcess?.providerDetails?.value.token;
      if (contextToken) {
        webSdkElement.setToken(contextToken);
      }
    },
    [events, refetch]
  );

  const embeddedWebSdkProps = useMemo(
    () =>
      ({
        // oxlint-disable-next-line typescript/consistent-type-assertions
        locale: locale as Locale,
        token,
        events: {
          desktop: {
            onOnboardingStarted: handleOnboardingStarted,
            onOnboardingCompleted: handleOnboardingCompleted,
            onOnboardingCompletedError: handleOnboardingCompletedError,
            onOnboardingRestartRequest: handleOnboardingRestartRequest,
          },
        },
      }) satisfies EmbeddedWebSdk,
    [
      locale,
      token,
      handleOnboardingStarted,
      handleOnboardingCompleted,
      handleOnboardingCompletedError,
      handleOnboardingRestartRequest,
    ]
  );

  if (processStatus === KycProcessStatus.submitted) return null;

  if (isLoading) return <FourthlineLoading isFullHeight={false} />;

  if (!token || !redirectUrl) {
    sentry.captureException(new Error('Fourthline KYC credentials missing'), {
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus: kycInfo.activeProcess?.processStatus,
        hasProviderDetails: Boolean(providerDetails),
      },
      tags: {
        component: 'FourthlineProvider',
        provider: KycProvider.fourthline,
      },
      cft: CFT.IDENTITY_VERIFICATION,
    });

    return <KycError onRetry={refetch} />;
  }

  const isExpired = isKycExpired(kycInfo);
  const isQesRequired = Boolean(kycInfo.activeProcess?.signatureRequired);

  return (
    <>
      <Fourthline
        embeddedWebSdkProps={embeddedWebSdkProps}
        fourthlineRedirectUrl={redirectUrl}
        isKycExpired={isExpired}
        isKycRequired
        isQesRequired={isQesRequired}
        legalCountry={legalCountry}
        mode={trackingPrefix}
        onBack={onBack}
      />
      {canSkipKyc ? (
        <Button
          className={styles.qaButton}
          onClick={() => {
            onComplete(KycProvider.fourthline);
          }}
          stretch
          type="button"
          variant="secondary"
        >
          <FormattedMessage
            defaultMessage="QA Tool | Complete KYC"
            id="identity-verification-kit.kyc.skip.cta"
          />
        </Button>
      ) : null}
    </>
  );
}
